import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { FadeInZoom } from 'app/components/Common/Animation/FadeInZoom'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import React, { memo } from 'react'
import { LazyLoadComponent } from 'react-lazy-load-image-component'
import { Parallax, ParallaxProvider } from 'react-scroll-parallax'

export interface Props {
  imageBig?: ImageProps
  imageSmall?: ImageProps
  imageMedium?: ImageProps
  pretitle?: string
  title?: string
  subtitle?: string
  description?: string
}

export const Intro = memo(function Intro({
  imageBig,
  imageSmall,
  imageMedium,
  pretitle,
  title,
  subtitle,
  description,
}: Props) {
  let variant = 'no-image'

  if (imageMedium) variant = 'image-medium'
  if (imageBig) variant = 'image-big'

  return (
    <Container className="intro-block">
      <Wrapper>
        <IntroLeft className="item-intro-left" variant={variant}>
          <IntroLeftText>
            <Deco />
            {pretitle ? (
              <FadeInUp>
                <Pretitle
                  dangerouslySetInnerHTML={{ __html: pretitle }}
                ></Pretitle>
              </FadeInUp>
            ) : null}
            {title ? (
              <FadeInUp>
                <Title dangerouslySetInnerHTML={{ __html: title }}></Title>
              </FadeInUp>
            ) : null}
            {subtitle ? (
              <FadeInUp>
                <Subtitle
                  dangerouslySetInnerHTML={{ __html: subtitle }}
                ></Subtitle>
              </FadeInUp>
            ) : null}
            {description ? (
              <FadeInUp>
                <Text
                  dangerouslySetInnerHTML={{ __html: description }}
                  variant={variant}
                ></Text>
              </FadeInUp>
            ) : null}
          </IntroLeftText>

          {imageSmall ? (
            <FadeInZoom>
              <ImageSmall {...imageSmall} />
            </FadeInZoom>
          ) : null}
        </IntroLeft>
        {imageMedium || imageBig ? (
          <ParallaxProvider>
            <IntroRight className="item-intro-right">
              {imageMedium ? (
                <ImageMediumWrapper>
                  <FadeInZoom>
                    <ImageWrapper>
                      <Parallax translateY={['-20%', '20%']}>
                        <ImageMedium {...imageMedium} />
                      </Parallax>
                    </ImageWrapper>
                  </FadeInZoom>
                </ImageMediumWrapper>
              ) : null}
              {imageBig ? (
                <FadeInZoom>
                  <ImageWrapper>
                    <Parallax translateY={['-20%', '20%']}>
                      <ImageBig {...imageBig} />
                    </Parallax>
                  </ImageWrapper>
                </FadeInZoom>
              ) : null}
            </IntroRight>
          </ParallaxProvider>
        ) : null}
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  position: relative;
  width: 100%;
  margin: 8vw 0;

  @media (max-width: 991px) {
    margin: 30px 0;
  }
`

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;

  @media (max-width: 991px) {
    display: block;
  }
`

const Deco = styled.div`
  position: absolute;
  top: -6vw;
  left: -6vw;
  width: 13vw;
  height: 150%;
  min-height: 25vw;
  background-color: #ffffff;

  @media (max-width: 991px) {
    top: -40px;
    left: -30px;
    width: 120px;
    height: 120%;
  }
`

const IntroLeft = styled.div<{ variant: string }>`
  width: 100%;
  position: relative;
  padding: 6vw 10vw 0vw 16vw;

  ${({ variant }) => {
    switch (variant) {
      case 'image-big':
      case 'image-medium':
        return css`
          width: 50%;
        `
    }
  }}

  @media (max-width: 991px) {
    padding: 60px;
    width: 100% !important;
  }
`
const IntroLeftText = styled.div`
  position: relative;
`

const Title = styled.h2`
  font-weight: 400;
  font-size: 3vw;
  font-family: ${({ theme }) => theme.fontFamily.heading};
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  display: block;
  text-transform: uppercase;
  position: relative;

  @media (max-width: 991px) {
    font-size: 28px;
  }
`
const Subtitle = styled.h4`
  font-weight: 200;
  font-size: 2vw;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.subheading};
  position: relative;

  @media (max-width: 991px) {
    font-size: 22px;
  }
`
const Pretitle = styled.h3`
  font-size: 0.9vw;
  letter-spacing: 2px;
  margin-bottom: 3vw;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.variants.primaryLight};
  font-family: ${({ theme }) => theme.fontFamily.subheading};
  position: relative;

  @media (max-width: 1439px) {
    font-size: 14px;
    margin-bottom: 20px;
  }
`

const Text = styled.div<{ variant: string }>`
  max-width: 100%;
  width: 950px;
  margin-top: 3vw;
  position: relative;
  ${({ variant }) => {
    switch (variant) {
      case 'image-big':
      case 'image-medium':
        return css`
          width: 450px;
        `
    }
  }};

  @media (max-width: 991px) {
    margin-top: 20px;
    width: 100% !important;
  }
`

const IntroRight = styled.div`
  width: 50%;
  position: relative;

  @media (max-width: 991px) {
    width: 100%;
    margin-top: 40px;
    overflow: hidden;
  }
`
const ImageBigWrapper = styled.div``

const ImageWrapper = styled.div`
  overflow: hidden;
  position: relative;
`

const ImageMediumWrapper = styled.div`
  top: 0;
  position: absolute;
  right: 8vw;
  width: 32vw;
  height: auto;
  z-index: 3;
`
const ImageMedium = styled(Image)`
  img {
    width: 100%;
    height: auto;
  }

  @media (max-width: 991px) {
    display: none;
  }
`
const ImageBig = styled(Image)`
  img {
    width: 100%;
    height: auto;
  }

  @media (max-width: 991px) {
    display: block;
    width: 100%;
    overflow: hidden;
    padding: 30px;
    aspect-ratio: 1;
  }
`
const ImageSmallWrapper = styled.div`
  overflow: hidden;
`
const ImageSmall = styled(Image)`
  margin-top: 3vw;
  display: block;
  img {
    width: 50%;
    height: auto;
  }

  @media (max-width: 991px) {
    display: none;
  }
`
